import { useMemo } from 'react';
import type { DropResult } from 'react-beautiful-dnd';

import { isPodPreviewDroppableId, parsePodPreviewDroppableId } from '../AdPodPreviewSection/adPodPreviewUtils';
import { UNSEQUENCED_TABLE_ID } from '../constants';
import { useSequenceContext } from '../context';
import type { SequenceAdV5 } from '../types';
import useSequenceForm from './useSequenceForm';

export type UseSequenceContent = {
  filteredUnsequencedAds: SequenceAdV5[];
  handleDragEnd: (dropResult: DropResult) => void;
};

const useSequenceContent = (): UseSequenceContent => {
  const { sequenceState } = useSequenceContext();
  const { searchUnsequencedAdsTerm } = sequenceState;

  const { values, changeAdSequenceOrder, movePodAd } = useSequenceForm();
  const { sequences, unsequencedAds } = values;

  // filtered unseqeunced ads according to search term
  const filteredUnsequencedAds = useMemo((): SequenceAdV5[] => {
    if (!searchUnsequencedAdsTerm) return unsequencedAds;

    const loweredSearchTerm = searchUnsequencedAdsTerm.toLowerCase();

    return unsequencedAds.reduce((acc, ad) => {
      const { name, trackingId } = ad;

      const loweredName = name?.toLowerCase();
      const loweredTrackingId = trackingId?.toLowerCase();

      if (
        (loweredTrackingId && loweredTrackingId.includes(loweredSearchTerm)) ||
        loweredName.includes(loweredSearchTerm)
      ) {
        acc.push(ad);
      }

      return acc;
    }, new Array<SequenceAdV5>());
  }, [searchUnsequencedAdsTerm, unsequencedAds]);

  // handle drag and drop on sequence tables
  const handleDragEnd = (dropResult: DropResult): void => {
    const { destination, source } = dropResult;

    if (!destination) return;

    const isDestPodPreview = isPodPreviewDroppableId(destination.droppableId);
    const isSourcePodPreview = isPodPreviewDroppableId(source.droppableId);

    // sequence tables and the ad pod preview are isolated drag systems via
    // the `type` prop on their Droppables; this guards against cross-zone
    // drops in case the types ever drift
    if (isDestPodPreview !== isSourcePodPreview) return;

    if (isDestPodPreview && isSourcePodPreview) {
      const { seqIndex, podPosition: destPodPosition, alignment: destAlignment } = parsePodPreviewDroppableId(
        destination.droppableId
      );
      const {
        seqIndex: srcSeqIndex,
        podPosition: srcPodPosition,
        alignment: srcAlignment,
      } = parsePodPreviewDroppableId(source.droppableId);

      if (
        srcSeqIndex === seqIndex &&
        srcPodPosition === destPodPosition &&
        srcAlignment === destAlignment &&
        source.index === destination.index
      ) {
        return;
      }

      // moves between sequences are not supported in the pod preview today
      if (srcSeqIndex !== seqIndex) return;

      const sourceAds = sequences[seqIndex]?.ads.filter(
        (ad) => ad.adSequence?.podPosition === srcPodPosition && ad.adSequence?.podAlignment === srcAlignment
      );
      const draggedAd = sourceAds?.[source.index];

      if (draggedAd) {
        movePodAd({
          seqIndex,
          adId: draggedAd.id,
          destPodPosition,
          destAlignment,
          destIndex: destination.index,
        });
      }

      return;
    }

    const { droppableId: sourceDroppableId, index: sourceIndex } = source;
    const isUnsequencedSource = sourceDroppableId === UNSEQUENCED_TABLE_ID;

    // find ad, that was dragged from a table
    const draggedAd = isUnsequencedSource
      ? filteredUnsequencedAds[sourceIndex]
      : sequences[Number(sourceDroppableId)].ads[sourceIndex];

    // handle situations, when indexes from filtered unsequenced array are not the same as in the original array
    if (isUnsequencedSource) {
      source.index = unsequencedAds.findIndex(({ id }) => id === draggedAd.id);
    }

    changeAdSequenceOrder({ destination, source, draggedAd });
  };

  return {
    filteredUnsequencedAds,
    handleDragEnd,
  };
};

export default useSequenceContent;
